import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../../../../services/api";
import styles from "./RetryDiscordButton.module.scss";

interface RetryDiscordButtonProps {
  lobbyId: string;
  disabled: boolean;
  onRetry: () => void;
}

const COOLDOWN_SECONDS = 30;

const RetryDiscordButton: React.FC<RetryDiscordButtonProps> = ({
  lobbyId,
  disabled,
  onRetry,
}) => {
  const [cooldown, setCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const startCooldown = useCallback(() => {
    setCooldown(COOLDOWN_SECONDS);
    intervalRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleRetry = async () => {
    setLoading(true);
    try {
      await api.lobby.retryDiscordChannel(lobbyId);
      onRetry();
    } catch {
      // Even on failure, start cooldown to match server-side rate limit
    } finally {
      setLoading(false);
      startCooldown();
    }
  };

  const isDisabled = disabled || loading || cooldown > 0;

  const getLabel = () => {
    if (loading) return "Retrying...";
    if (cooldown > 0) return `Try again in ${cooldown}s`;
    return "Retry Discord Channel";
  };

  return (
    <button
      type="button"
      className={styles.retryButton}
      onClick={handleRetry}
      disabled={isDisabled}
      aria-label="Retry Discord channel creation"
    >
      <svg
        className={styles.icon}
        viewBox="0 0 20 20"
        fill="currentColor"
        width="16"
        height="16"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M15.312 11.424a5.5 5.5 0 01-9.201 2.466l-.312-.311h2.433a.75.75 0 000-1.5H4.568a.75.75 0 00-.75.75v3.664a.75.75 0 001.5 0v-2.18l.31.31A7 7 0 0016.76 11.04a.75.75 0 10-1.448-.632zM4.688 8.576a5.5 5.5 0 019.201-2.466l.312.311H11.77a.75.75 0 000 1.5h3.664a.75.75 0 00.75-.75V3.507a.75.75 0 00-1.5 0v2.18l-.31-.31A7 7 0 003.24 8.96a.75.75 0 101.448.632z"
          clipRule="evenodd"
        />
      </svg>
      {getLabel()}
    </button>
  );
};

export default RetryDiscordButton;
